import "./SignIn.css";
import { ReactComponent as FrameBack } from "images/frame_back.svg";
import { ReactComponent as MainLogo } from "images/main-logo.svg";
import { ReactComponent as UserIcon } from "images/person.svg";
import { ReactComponent as ArrowLeft } from "images/arrow_left.svg";
import { TextField } from "@material-ui/core";
import { Link, useLocation, useNavigate, Location } from "react-router-dom";
import { ChangeEvent, FocusEvent, useEffect, useRef, useState } from "react";
import { populateUserSession, signIn, signInMfa } from "../../services/authUtil";
import { isSetup } from "../../services/students";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import TalaeraSpinner from "../../components/TalaeraSpinner/TalaeraSpinner";
import {
  getCustomErrorComponent,
  getErrorComponents,
  getSuccessComponents,
} from "../../services/util";
import useSignInForm from "./utils";
import AuthRedirect from "../../components/auth/components/AuthRedirect/AuthRedirect";
import { PasswordInput } from "../../components/PasswordInput/PasswordInput";
import { isPending } from "../../services/users";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { OAuthProvider, OAuthStep } from "model/oauth";
import GoogleButton from "components/AuthButtons/GoogleButton";
import LinkedinButton from "components/AuthButtons/LinkedInButton";
import MetaButton from "components/AuthButtons/MetaButton";
import OrDivider from "components/auth/components/OrDivider";

export type SignInLocationState = {
  resetDone?: boolean;
  token?: string;
  mfaToken?: string;
  email?: string;
  error?: Errors;
  sessionEpired?: boolean;
};

type Errors = string | { statusCode: number; errors: [{ description: string }] };

const SignIn = () => {
  const navigate = useNavigate();
  const location: Location<SignInLocationState> = useLocation();
  const [mfaToken, setMfaToken] = useState(location.state?.mfaToken);
  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    setTouched,
    setMfaMode,
    onValidate,
    onTouched,
  } = useSignInForm({
    email: location.state?.email ?? "",
    password: mfaToken ? undefined : "",
    code: "",
  });
  const codeRef = useRef<HTMLInputElement>(null);
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Errors | undefined>(location.state?.error);
  const [resetDone, setResetDone] = useState(location.state?.resetDone ?? false);
  const [wasSessionExpired] = useState(location.state?.sessionEpired ?? false);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (isValidated) {
      setResetDone(false);
      setErrors("");
      setLoading(true);
      signIn(
        {
          email: account.email,
          password: account.password,
        },
        (resp: { mfa: boolean; token: string }) => {
          if (resp.mfa) {
            setMfaToken(resp.token);
            setMfaMode(true);
            codeRef.current?.focus();
            setLoading(false);
          } else {
            handleFinishSignIn();
          }
        },
        (error: any) => {
          setLoading(false);
          setErrors(error?.errors || error);
        }
      );
    }
  };

  const handleMfaSubmit = () => {
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
      code: true,
    });

    if (isValidated) {
      setErrors("");
      setLoading(true);
      signInMfa(
        {
          email: account.email,
          token: mfaToken,
          code: account.code,
        },
        () => {
          handleFinishSignIn();
        },
        (error: any) => {
          setLoading(false);
          setErrors(error);
        }
      );
    }
  };

  const handleFinishSignIn = () => {
    populateUserSession(
      (user: any) => {
        if (user && user.student && isPending(user.status) && location.state?.token) {
          navigate(TalaeraRoutes.buildVerifySignupIndividual(location.state?.token));
        } else if (user && user.student && isSetup(user.student?.status)) {
          navigate(TalaeraRoutes.buildLibraryRootPath());
        } else {
          if (location.pathname === TalaeraRoutes.SIGN_IN.path) {
            navigate(TalaeraRoutes.ROOT.path + (location.hash || ""));
          } else {
            setRedirectToReferrer(true);
          }
        }
        setLoading(false);
      },
      (error: any) => {
        setErrors(error);
        setLoading(false);
      }
    );
  };

  const handleBack = () => {
    setMfaMode(false);
    setErrors("");
    setMfaToken(undefined);
  };

  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  let errorComp;
  if (errors) {
    if (typeof errors === "string") {
      errorComp = getErrorComponents(errors);
    } else if (errors.statusCode === 410) {
      errorComp = getCustomErrorComponent(
        <span>
          Account has been deleted. Please contact{" "}
          <a href="mailto:scheduling@talaera.com">customer support</a>
        </span>
      );
    } else {
      errorComp = getErrorComponents(errors);
    }
  }

  const onSocialAuthClick = (provider: OAuthProvider) => {
    return () => navigate(TalaeraRoutes.buildSignInOAuthPath(provider, OAuthStep.Start));
  };

  return (
    <AuthRedirect redirectToReferrer={redirectToReferrer} location={location}>
      <Helmet htmlAttributes={{ lang: i18n.language }}>
        <title>Talaera | Sign in</title>
        <meta
          name="description"
          content="This page is for user to sign in with their information"
        />
      </Helmet>

      <div className="sign-in-page">
        <div className="sign-in-content">
          <MainLogo className="logo" />

          {wasSessionExpired && (
            <div className="talaera-error">{t("Signin.sessionExpiredMessage")}</div>
          )}

          {errorComp && <div className="sign-in-error">{errorComp}</div>}

          {resetDone && (
            <div className={"sign-in-success"}>
              {getSuccessComponents(
                "Your password has been reset. Please sign in with your new password."
              )}
            </div>
          )}

          <div className="sign-in-header">
            <UserIcon />
            <h1>{t(mfaToken ? "Signin.Enter code" : "Signin.Sign In")}</h1>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-wrapper">
              <TextField
                name="email"
                disabled={!!mfaToken}
                label={t("Signin.Email address")}
                placeholder="johnsmith@gmail.com"
                variant="outlined"
                value={account.email}
                error={!!(formErrors.email && touched.email)}
                helperText={touched.email && formErrors.email}
                onChange={handleChangeField}
                onBlur={handleBlurField}
              />
              {mfaToken ? (
                <>
                  <TextField
                    inputRef={codeRef}
                    name="code"
                    className="sign-in-code-input"
                    label={t("Signin.Code")}
                    type="number"
                    variant="outlined"
                    placeholder={t("Signin.Enter code")}
                    value={account.code}
                    error={!!(formErrors.code && touched.code)}
                    helperText={touched.code && formErrors.code}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                  />
                </>
              ) : (
                <>
                  <PasswordInput
                    name="password"
                    label={t("Signin.Password")}
                    type="password"
                    variant="outlined"
                    placeholder={t("Signin.Enter your password")}
                    value={account.password}
                    error={!!(formErrors.password && touched.password)}
                    helperText={touched.password && formErrors.password}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                  />
                </>
              )}
            </div>
            {!mfaToken && (
              <div className="sign-in-forgot">
                <Link to={TalaeraRoutes.FORGOT.path}>{t("Signin.forgotPassword")}</Link>
              </div>
            )}

            {loading ? (
              <div className="sign-in-spinner">
                <TalaeraSpinner />
              </div>
            ) : mfaToken ? (
              <button className="btn submit-code-btn" onClick={handleMfaSubmit}>
                {t("Signin.Submit Code")}
              </button>
            ) : (
              <button className="btn" type="submit">
                {t("Signin.Sign In")}
              </button>
            )}
          </form>

          {mfaToken ? (
            <div>
              {/* eslint-disable-next-line */}
              <a onClick={handleBack}>
                <ArrowLeft />
                {t("Common.Back")}
              </a>
            </div>
          ) : (
            <div className="other-actions-container">
              <Link to={TalaeraRoutes.SIGN_IN_SSO.path}>Sign In with SSO</Link>
              <OrDivider />
              <GoogleButton onClick={onSocialAuthClick(OAuthProvider.GOOGLE)}>
                Sign in with Google
              </GoogleButton>
              <LinkedinButton onClick={onSocialAuthClick(OAuthProvider.LINKEDIN)}>
                Sign in with LinkedIn
              </LinkedinButton>
              <MetaButton onClick={onSocialAuthClick(OAuthProvider.META)}>
                Sign in with Facebook
              </MetaButton>
              <Link to="/self_sign_up">{t("Signin.signUpHere")}</Link>
            </div>
          )}
        </div>

        <FrameBack className="sign-in-frame-back" />
      </div>
    </AuthRedirect>
  );
};

export default SignIn;
